import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { Account, AccountSearchParams } from '../types/account';
import { accountService } from '../services/api';
import AccountSearchBar from '../components/AccountSearchBar';
import {
  formatCurrency,
  getAccountTypeLabel,
  getAccountTypeBadgeColor,
  getAccountStatusBadge,
  exportToCSV,
  formatDate,
} from '../utils/accountUtils';

const AccountManagement: React.FC = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalElements, setTotalElements] = useState(0);
  const [searchParams, setSearchParams] = useState<AccountSearchParams>({});

  const loadAccounts = async (params: AccountSearchParams = searchParams, currentPage = page) => {
    try {
      setLoading(true);
      setError(null);
      const response = await accountService.searchAccounts({
        ...params,
        page: currentPage,
        size: rowsPerPage,
      });
      setAccounts(response.content);
      setTotalElements(response.totalElements);
    } catch (err: any) {
      console.error('Error loading accounts:', err);
      setError(err.response?.data?.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts();
  }, [page, rowsPerPage]);

  const handleSearch = (params: AccountSearchParams) => {
    setSearchParams(params);
    setPage(0);
    loadAccounts(params, 0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExport = () => {
    const exportData = accounts.map((account) => ({
      'Account Number': account.accountNumber,
      'Account Name': account.accountName,
      'Account Type': getAccountTypeLabel(account.accountType),
      'Partner Name': account.partnerName || 'N/A',
      Currency: account.currency,
      'Current Balance': account.currentBalance,
      'Available Balance': account.availableBalance,
      Status: account.isActive ? 'Active' : 'Inactive',
      'Date Created': formatDate(account.dateCreated),
    }));
    exportToCSV(exportData, `accounts-${new Date().toISOString().split('T')[0]}.csv`);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Account Management</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={accounts.length === 0}
          >
            Export
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadAccounts()}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper sx={{ mb: 3, p: 2 }}>
        <AccountSearchBar onSearch={handleSearch} showFilters={true} />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Account Number</TableCell>
                <TableCell>Account Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Partner</TableCell>
                <TableCell>Currency</TableCell>
                <TableCell align="right">Current Balance</TableCell>
                <TableCell align="right">Available Balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : accounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary">No accounts found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map((account) => {
                  const statusBadge = getAccountStatusBadge(account);
                  return (
                    <TableRow
                      key={account.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/accounts/${account.accountNumber}`)}
                    >
                      <TableCell>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/accounts/${account.accountNumber}`);
                          }}
                        >
                          {account.accountNumber}
                        </Link>
                      </TableCell>
                      <TableCell>{account.accountName}</TableCell>
                      <TableCell>
                        <Chip
                          label={getAccountTypeLabel(account.accountType)}
                          color={getAccountTypeBadgeColor(account.accountType)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {account.partnerName ? (
                          <Link
                            component="button"
                            variant="body2"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (account.partnerId) {
                                navigate(`/partners/${account.partnerId}`);
                              }
                            }}
                          >
                            {account.partnerName}
                          </Link>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Control Account
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={account.currency} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(account.currentBalance, account.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatCurrency(account.availableBalance, account.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={statusBadge.label} color={statusBadge.color} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/accounts/${account.accountNumber}`);
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default AccountManagement;
